/**
 * Farm rules made quick to switch on and off. A rule is a program that starts on readings
 * ("when Irrigator 2 is off for 10 s, switch Pumps 1–3 off"), the same thing FMS-Control called
 * a control rule. Farmers pair pumps with irrigators differently from one day to the next, so
 * the app lets them flick rules on and off beside the output they're switching, and make a new
 * one in a line: "switch these off when that switches off".
 *
 * Switching is still done by the program runner (packages/core/src/programs.ts), only once the
 * board confirms, and only on farms Think-Control runs.
 */
import type { Action, Program, RuleCondition } from "./programs.ts";

/** Started by readings only (no schedule or set time): a rule rather than a timed program. */
export function isRule(p: Pick<Program, "start">): boolean {
  return p.start.length > 0 && p.start.every((c) => c.type === "rule");
}

/** An FMS board output as the app names it ("ro1"), or a Milesight output by channel name. */
export function outputKey(a: { kind: string; channel: number; port?: string }): string {
  return a.port ?? `${a.kind}${a.channel}`;
}

/** Every output a program switches (start and stop actions, steps), as "devEui:key". */
export function outputsOf(p: Pick<Program, "startActions" | "stopActions" | "steps">): string[] {
  return [...new Set([...p.startActions, ...p.stopActions, ...p.steps].map((a) => `${a.devEui}:${outputKey(a)}`))];
}

/** Does this program switch that output? */
export function switchesOutput(p: Pick<Program, "startActions" | "stopActions" | "steps">, devEui: string, key: string): boolean {
  return outputsOf(p).includes(`${devEui}:${key}`);
}

/** Does this program watch that reading (a start or stop condition)? */
export function watches(p: Pick<Program, "start" | "stopConditions">, devEui: string, port: string): boolean {
  const conds: RuleCondition[] = [...p.stopConditions];
  for (const c of p.start) if (c.type === "rule") conds.push(c, ...(c.also ?? []));
  return conds.some((c) => c.devEui === devEui && c.port === port);
}

type Label = (devEui: string, port: string) => string;

function when(c: RuleCondition, label: Label): string {
  const what =
    c.op === "on"
      ? "switches on"
      : c.op === "off"
        ? "switches off"
        : c.op === "between"
          ? `is between ${c.value} and ${c.value2}`
          : `goes ${c.op === ">" ? "above" : "below"} ${c.value}`;
  return `${label(c.devEui, c.port)} ${what}${c.forSec ? ` (for ${c.forSec} s)` : ""}`;
}

/** "When Irrigator 2 switches off → Pump 1 off, Pump 2 off" */
export function ruleSummary(p: Pick<Program, "start" | "startActions" | "steps">, label: Label): string {
  const conds = p.start.flatMap((c) => (c.type === "rule" ? [[c, ...(c.also ?? [])].map((x) => when(x, label)).join(" and ")] : []));
  const acts = p.startActions.map((a) => `${label(a.devEui, outputKey(a))} ${a.state === undefined ? `to ${a.value}` : a.state ? "on" : "off"}`);
  const steps = p.steps.map((s) => `${label(s.devEui, outputKey(s))} on ${s.untilStop ? "until stopped" : `${s.runMin} min`}`);
  return `When ${conds.join(", or ") || "…"} → ${[...acts, ...steps].join(", ") || "nothing"}`;
}

export interface QuickRule {
  /** What to watch: an on/off input or output. */
  watch: { devEui: string; port: string };
  /** "off": when it switches off; "on": when it switches on. */
  when: "on" | "off";
  /** How long it must stay that way first (stops a blip from switching pumps). */
  forSec: number;
  /** Outputs on FMS boards to switch. */
  outputs: Array<{ devEui: string; kind: "do" | "ro"; channel: number }>;
  /** What to do to them. */
  turn: "on" | "off";
  /** Switch the rule itself off once it has done its job (pairings change day to day). */
  offAfterRun: boolean;
}

/** A program for "switch these off when that switches off", named in plain words. */
export function quickRuleProgram(q: QuickRule, label: Label, timezone: string): Omit<Program, "id" | "accountId" | "updatedAt" | "updatedBy"> {
  const outs = q.outputs.map((o) => label(o.devEui, `${o.kind}${o.channel}`));
  const name = `${outs.join(", ")} ${q.turn} when ${label(q.watch.devEui, q.watch.port)} goes ${q.when}`.slice(0, 60);
  const startActions: Action[] = q.outputs.map((o) => ({ devEui: o.devEui, kind: o.kind, channel: o.channel, state: q.turn === "on", waitSec: 0 }));
  return {
    name,
    enabled: true,
    timezone,
    start: [{ type: "rule", devEui: q.watch.devEui, port: q.watch.port, op: q.when, forSec: q.forSec }],
    startActions,
    steps: [],
    stopConditions: [],
    stopActions: [],
    ...(q.offAfterRun ? { offAfterRun: true } : {}),
  };
}
